import * as React from "react";

import { ErrorState } from "@/components/ui/error-state";

interface Props {
  children: React.ReactNode;
  /** Custom fallback. Default = full-screen ErrorState với retry. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Generic React error boundary với reset retry.
 *
 * Doc 31. Wrap toàn app sau auth check để catch render crash trước khi
 * user thấy white screen.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
    // Future: send to Sentry/telemetry endpoint
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return <ErrorState fullScreen error={error} onRetry={this.reset} />;
    }
    return this.props.children;
  }
}
